import React, { useEffect, useRef } from 'react';
import { Tooltip, Button, Stack } from '@mui/material';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { ChevronLeft, ChevronRight, Send, SaveAltSharp } from '@mui/icons-material';

import { SaveAction } from '../../types';
import { STEP_CONFIG } from '../../constants';
import { getNextStep, getStepIndex } from '../../lib';
import { useDealBuilderContext } from '../../context';
import { useDealBuilderStepNavigation, useValidateAndAdvance, useDealBuilderPreviewAndSubmit } from '../../hooks';

interface Props {
  quoteId: string;
  proposalId: string;
  action?: SaveAction;
}

export const StepFooterNav = ({ quoteId, proposalId, action }: Props) => {
  const { viewStep, goToPrevStep } = useDealBuilderStepNavigation();
  const { handleCreateDealBuilderProposal, isSubmitLoading } = useDealBuilderPreviewAndSubmit();

  const {
    startNewQuote,
    equipment: { isEquipmentSaving },
    sourceData: { dynamicsVOId, voId },
  } = useDealBuilderContext();

  const hasSavedCurrentStep = useRef(false);

  useEffect(() => {
    hasSavedCurrentStep.current = false;
  }, [viewStep]);

  const runSaveAction = async () => {
    try {
      await action?.fn({ quoteId, proposalId, voId, dynamicsVOId });
      hasSavedCurrentStep.current = true;
    } catch {
      console.error('Error while saving current step');
    }
  };

  const validateAndAdvance = useValidateAndAdvance(() => {
    if (hasSavedCurrentStep.current) return;
    runSaveAction();
  });

  const stepIndex = getStepIndex(viewStep);
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === STEP_CONFIG.length - 1;
  const nextStepConfig = STEP_CONFIG[getStepIndex(getNextStep(viewStep))];

  const handleSubmitClick = async () => {
    const linkToProposalPage = await handleCreateDealBuilderProposal();

    if (linkToProposalPage) window.open(linkToProposalPage, '_blank');
  };

  return (
    <Stack direction="row" justifyContent="space-between">
      <Button disabled={isFirstStep} startIcon={<ChevronLeft />} onClick={goToPrevStep}>
        Back
      </Button>

      <Stack direction="row" gap={1}>
        <RenderWithCondition
          condition={!isLastStep}
          fallBack={
            <>
              <Button variant="outlined" startIcon={<Send />} onClick={startNewQuote}>
                Save as Draft
              </Button>

              <Button variant="contained" endIcon={<Send />} loading={isSubmitLoading} onClick={handleSubmitClick}>
                Generate & Send Quote
              </Button>
            </>
          }
        >
          <>
            {action && (
              <Tooltip title={action?.info ?? ''} placement="top" arrow>
                <span>
                  <Button
                    loading={action.isLoading}
                    startIcon={<SaveAltSharp />}
                    disabled={action.isDisabled}
                    onClick={runSaveAction}
                  >
                    Save Changes
                  </Button>
                </span>
              </Tooltip>
            )}

            <Button
              variant="contained"
              endIcon={<ChevronRight />}
              loading={isEquipmentSaving}
              disabled={isEquipmentSaving}
              onClick={validateAndAdvance}
            >
              Continue to {nextStepConfig.label}
            </Button>
          </>
        </RenderWithCondition>
      </Stack>
    </Stack>
  );
};
